import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import produtoService from '../../services/produtoService';
import Toast from '../../components/Toast';
import { withPermissionAndLevel } from '../../components/withPermission';

const initialState = {
  nome: '',
  status: 'Ativo',
  valor: 0,
  tipoProduto: '',
  tipoRequerimento: '',
  tblcontas: '',
};

const ProdutoEdit = () => {
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [tiposProduto, setTiposProduto] = useState([]);
  const [contas, setContas] = useState([]);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      try {
        const [produtoResponse, tiposResponse, contasResponse] = await Promise.all([
          produtoService.getById(id),
          produtoService.getTipos(),
          produtoService.getContas()
        ]);
        
        if (produtoResponse.data) {
          setForm(produtoResponse.data);
        }
        setTiposProduto(tiposResponse.data || []);
        setContas(contasResponse.data || []);
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        showToast('Erro ao carregar dados', 'error');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast({ ...toast, show: false });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    setError('');
    // Limpar erro do campo específico
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setShowConfirm(true);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  if (loading) return <div className="max-w-lg mx-auto mt-10 text-blue-600">Carregando dados...</div>;
  if (deleteSuccess) return <div className="max-w-lg mx-auto mt-10 text-green-600">Produto excluído com sucesso!</div>;

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Produto</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Nome *</label>
          <input
            type="text"
            name="nome"
            value={form.nome}
            onChange={handleChange}
            maxLength={255}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.nome ? 'border-red-500' : ''}`}
            placeholder="Nome do produto"
          />
          {fieldErrors.nome && <p className="text-red-500 text-xs mt-1">{fieldErrors.nome}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Status *</label>
          <select
            name="status"
            value={form.status}
            onChange={handleChange}
            className={`w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400 ${fieldErrors.status ? 'border-red-500' : ''}`}
          >
            <option value="">Selecione o status</option>
            <option value="Ativo">Ativo</option>
            <option value="Inativo">Inativo</option>
          </select>
          {fieldErrors.status && <p className="text-red-500 text-xs mt-1">{fieldErrors.status}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Valor</label>
          <input
            type="number"
            name="valor"
            value={form.valor}
            onChange={handleChange}
            min={0}
            step={0.01}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Valor (R$)"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo Produto *</label>
          <select
            name="tipoProduto"
            value={form.tipoProduto}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione o tipo de produto</option>
            {tiposProduto.map(tipo => (
              <option key={tipo.idTipoProduto} value={tipo.idTipoProduto}>
                {tipo.nome}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="tipoRequerimento" className="block text-sm font-medium mb-1">
            Gera protocolo de requerimento
          </label>
          <select
            id="tipoRequerimento"
            name="tipoRequerimento"
            value={form.tipoRequerimento || ''}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Não gera protocolo</option>
            <option value="CERTIFICADO_HISTORICO">Certificado/Histórico</option>
            <option value="LEVANTAMENTO_NOTAS">Levantamento de Notas</option>
            <option value="CARTEIRINHA_CAPELANIA">Carteirinha - Capelania</option>
            <option value="DECLARACAO">Declaração</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Quando o pedido com este produto recebe baixa, o protocolo do tipo escolhido é gerado automaticamente.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Conta *</label>
          <select
            name="tblcontas"
            value={form.tblcontas}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="">Selecione a conta</option>
            {contas.map(conta => (
              <option key={conta.id} value={conta.id}>
                {conta.label}
              </option>
            ))}
          </select>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Produto atualizado com sucesso!</div>}
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/produtos')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={handleDelete} disabled={deleting} aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Edição"
        message="Deseja realmente salvar as alterações deste produto?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          setLoading(true);
          setError('');
          setSuccess(false);
          try {
            const response = await produtoService.update(id, form);
            setSuccess(true);
            showToast(response.message || 'Produto atualizado com sucesso!', 'success');
            setTimeout(() => navigate('/produtos'), 1500);
          } catch (err) {
            console.error('Erro ao atualizar:', err);
            const errorData = err.response?.data;
            
            // Se houver erros de validação específicos dos campos
            if (errorData?.validationErrors && Array.isArray(errorData.validationErrors)) {
              const errors = {};
              errorData.validationErrors.forEach(error => {
                if (error.path) {
                  // Se já existe um erro para esse campo, mantém o primeiro (mais relevante)
                  if (!errors[error.path]) {
                    errors[error.path] = error.msg;
                  }
                }
              });
              setFieldErrors(errors);
            } else {
              // Erro geral apenas no toast
              showToast(errorData?.message || 'Erro ao atualizar produto', 'error');
            }
          }
          setLoading(false);
        }}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir este produto? Esta ação não poderá ser desfeita."
        confirmType="delete"
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={async () => {
          setShowDeleteConfirm(false);
          setDeleting(true);
          try {
            const response = await produtoService.delete(id);
            setDeleteSuccess(true);
            showToast(response.message || 'Produto excluído com sucesso!', 'success');
            setTimeout(() => navigate('/produtos'), 1500);
          } catch (err) {
            const errorMessage = err.response?.data?.message || 'Erro ao excluir produto.';
            setError(errorMessage);
            showToast(errorMessage, 'error');
          }
          setDeleting(false);
        }}
      />
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default withPermissionAndLevel(
  ProdutoEdit, 
  'produtos.update', 
  3, 
  'Você não tem permissão para editar Produtos.'
);
